import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, CreditCard, Users, RefreshCcw, MessageCircle, RotateCcw, Mail, Plus, Trash2, GraduationCap, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_NOTE = '*A 6% service fee will be included for all debit and credit card payments.';

export const Settings = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cardFeeEnabled, setCardFeeEnabled] = useState(true);
  const [cardFeeNote, setCardFeeNote] = useState(DEFAULT_NOTE);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);

  // WhatsApp templates
  const [waTemplates, setWaTemplates] = useState(null);
  const [waTokens, setWaTokens] = useState({});
  const [waSavingKey, setWaSavingKey] = useState(null);

  // Email snippets
  const [snippets, setSnippets] = useState([]);
  const [snippetDraft, setSnippetDraft] = useState({ title: '', body: '' });
  const [snippetSaving, setSnippetSaving] = useState(false);
  const [editingSnippetId, setEditingSnippetId] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchWaTemplates();
    fetchSnippets();
  }, []);

  const fetchSnippets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/email-snippets`, {
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSnippets(data.snippets || []);
      }
    } catch (err) {
      logger.error('Fetch snippets error:', err);
    }
  };

  const handleSaveSnippet = async () => {
    if (!snippetDraft.title.trim() || !snippetDraft.body.trim()) {
      toast.error('Snippet needs a title and a body');
      return;
    }
    setSnippetSaving(true);
    try {
      if (editingSnippetId) {
        const res = await fetch(`${API_URL}/api/admin/email-snippets/${editingSnippetId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Basic ${btoa(':' + password)}` },
          body: JSON.stringify(snippetDraft),
        });
        if (!res.ok) throw new Error('save failed');
        toast.success('Snippet updated');
      } else {
        const res = await fetch(`${API_URL}/api/admin/email-snippets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Basic ${btoa(':' + password)}` },
          body: JSON.stringify(snippetDraft),
        });
        if (!res.ok) throw new Error('save failed');
        toast.success('Snippet saved');
      }
      setSnippetDraft({ title: '', body: '' });
      setEditingSnippetId(null);
      fetchSnippets();
    } catch (err) {
      logger.error('Save snippet error:', err);
      toast.error('Failed to save snippet');
    } finally {
      setSnippetSaving(false);
    }
  };

  const handleDeleteSnippet = async (snippetId) => {
    if (!window.confirm('Delete this snippet? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/email-snippets/${snippetId}`, {
        method: 'DELETE',
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
      });
      if (!res.ok) throw new Error('delete failed');
      toast.success('Snippet deleted');
      fetchSnippets();
      if (editingSnippetId === snippetId) {
        setSnippetDraft({ title: '', body: '' });
        setEditingSnippetId(null);
      }
    } catch (err) {
      logger.error('Delete snippet error:', err);
      toast.error('Failed to delete');
    }
  };

  const beginEditSnippet = (s) => {
    setEditingSnippetId(s.id);
    setSnippetDraft({ title: s.title, body: s.body });
  };

  const fetchWaTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/whatsapp-templates`, {
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setWaTemplates(data.templates || {});
      setWaTokens(data.tokens || {});
    } catch (err) {
      logger.error('Fetch WhatsApp templates error:', err);
    }
  };

  const handleWaSave = async (key) => {
    setWaSavingKey(key);
    try {
      const res = await fetch(`${API_URL}/api/admin/whatsapp-templates/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Basic ${btoa(':' + password)}` },
        body: JSON.stringify({ body: waTemplates[key] || '' }),
      });
      if (!res.ok) throw new Error('save failed');
      toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} template saved`);
    } catch (err) {
      logger.error('Save WA template error:', err);
      toast.error('Failed to save template');
    } finally {
      setWaSavingKey(null);
    }
  };

  const handleWaReset = async (key) => {
    if (!window.confirm(`Reset the "${key}" WhatsApp template to the default copy?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/whatsapp-templates/${key}/reset`, {
        method: 'POST',
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
      });
      if (!res.ok) throw new Error('reset failed');
      const data = await res.json();
      setWaTemplates(data.templates);
      toast.success('Template reset to default');
    } catch (err) {
      logger.error('Reset WA template error:', err);
      toast.error('Failed to reset template');
    }
  };

  useEffect(() => {}, []);  // no-op — placeholder for future effects

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
      });
      if (res.status === 401) {
        navigate('/admin/login');
        return;
      }
      const data = await res.json();
      setCardFeeEnabled(!!data.card_fee_enabled);
      setCardFeeNote(data.card_fee_note || DEFAULT_NOTE);
      setLastUpdated(data.updated_at || null);
    } catch (err) {
      logger.error('Fetch settings error:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(':' + password)}`,
        },
        body: JSON.stringify({
          card_fee_enabled: cardFeeEnabled,
          card_fee_note: cardFeeNote.trim() || DEFAULT_NOTE,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      const data = await res.json();
      setLastUpdated(data.updated_at || null);
      toast.success('Settings saved — will apply to all new PDFs and emails.');
    } catch (err) {
      logger.error('Save settings error:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRebuildClientDirectory = async () => {
    const ok = window.confirm(
      'Rebuild the Client Directory?\n\n' +
      'This will scan every quote, invoice and receipt and idempotently add ' +
      'any missing clients (by email) to your CRM directory. Existing records ' +
      'are updated with the latest contact info. Safe to run repeatedly.'
    );
    if (!ok) return;
    setRebuilding(true);
    setRebuildResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/rebuild-client-directory`, {
        method: 'POST',
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
      });
      if (!res.ok) throw new Error('Rebuild failed');
      const data = await res.json();
      setRebuildResult(data);
      toast.success(`Directory rebuilt — ${data.unique_clients_upserted} unique client${data.unique_clients_upserted === 1 ? '' : 's'} synced.`);
    } catch (err) {
      logger.error('Rebuild directory error:', err);
      toast.error('Failed to rebuild directory');
    } finally {
      setRebuilding(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center text-foreground/60">Loading settings…</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/admin/dashboard')}
        className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground mb-4"
        data-testid="settings-back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Global toggles that apply to all quotes, invoices and emails.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Card Processing Fee Disclosure
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-foreground/70">
            This wording is appended to every quote and invoice — both the generated PDF and the client email — right below the payment / banking details.
          </p>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={cardFeeEnabled}
              onChange={(e) => setCardFeeEnabled(e.target.checked)}
              className="mt-1 w-4 h-4 accent-primary"
              data-testid="card-fee-enabled-toggle"
            />
            <span>
              <span className="font-medium text-foreground">Show the card fee disclosure on quotes & invoices</span>
              <span className="block text-xs text-foreground/60">Uncheck to hide it entirely (e.g. if you stop accepting cards).</span>
            </span>
          </label>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">
              Disclosure wording
            </label>
            <textarea
              value={cardFeeNote}
              onChange={(e) => setCardFeeNote(e.target.value)}
              disabled={!cardFeeEnabled}
              rows={3}
              maxLength={500}
              data-testid="card-fee-note-input"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:bg-gray-50 disabled:text-foreground/40"
              placeholder={DEFAULT_NOTE}
            />
            <div className="mt-1 flex justify-between text-xs text-foreground/50">
              <span>Rendered in italic amber on PDFs and in the payment details block on emails.</span>
              <span>{cardFeeNote.length}/500</span>
            </div>
          </div>

          {lastUpdated && (
            <p className="text-xs text-foreground/50">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => { setCardFeeNote(DEFAULT_NOTE); setCardFeeEnabled(true); }}
              className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-gray-50"
              data-testid="reset-defaults-btn"
            >
              Reset to defaults
            </button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-settings-btn"
              className="inline-flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Client Directory Maintenance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-foreground/70">
            Scan every quote, invoice and receipt and idempotently upsert every unique client (by email) into the CRM directory. Useful after upgrading — older quote-only clients aren&apos;t automatically in the directory, which means they won&apos;t appear in the &ldquo;Search existing clients&rdquo; autocomplete on the Create Quote / Create Invoice forms.
          </p>
          <p className="text-xs text-foreground/50">
            Safe to run repeatedly. Existing records are updated with the latest contact info; no duplicates are created.
          </p>

          {rebuildResult && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800" data-testid="rebuild-result">
              <div className="font-medium">Directory rebuilt successfully</div>
              <div className="text-xs mt-1">
                Scanned {rebuildResult.scanned} document{rebuildResult.scanned === 1 ? '' : 's'} · Upserted {rebuildResult.unique_clients_upserted} unique client{rebuildResult.unique_clients_upserted === 1 ? '' : 's'} · Skipped {rebuildResult.skipped_no_email} without email
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleRebuildClientDirectory}
              disabled={rebuilding}
              data-testid="rebuild-client-directory-btn"
              variant="outline"
              className="inline-flex items-center gap-2"
            >
              <RefreshCcw className={`w-4 h-4 ${rebuilding ? 'animate-spin' : ''}`} />
              {rebuilding ? 'Rebuilding…' : 'Rebuild Client Directory'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Snippets */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-primary" />
            Vet Training Flyer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-foreground/70 mb-4">
            Edit every word on the print-ready flyer — title, tagline, bullet lists, the two pricing tiers, trusted-partner benefits, and the contact block. Preview refreshes on save.
          </p>
          <Button
            onClick={() => navigate('/admin/vet-training-flyer/edit')}
            data-testid="settings-open-flyer-editor"
            className="inline-flex items-center gap-2"
          >
            Open flyer editor <ArrowRight className="w-4 h-4" />
          </Button>
        </CardContent>
      </Card>

      {/* Email Snippets */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Email Snippets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-foreground/70">
            Reusable email bodies staff can insert with one tap from the Compose Email screen. Great for common replies (introductions, quote follow-ups, apology / delay updates).
          </p>

          {/* Existing snippets */}
          {snippets.length === 0 ? (
            <p className="text-sm text-foreground/50 italic">No snippets yet. Add one below.</p>
          ) : (
            <div className="space-y-2">
              {snippets.map((s) => (
                <div key={s.id} className="border border-border rounded-lg p-3 bg-gray-50/50 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{s.title}</div>
                    <div className="text-xs text-foreground/60 truncate" dangerouslySetInnerHTML={{ __html: s.body.replace(/<[^>]+>/g, ' ').slice(0, 140) }} />
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => beginEditSnippet(s)} data-testid={`edit-snippet-${s.id}`} className="text-xs px-2 py-1 rounded border border-border hover:bg-white">Edit</button>
                    <button onClick={() => handleDeleteSnippet(s.id)} data-testid={`delete-snippet-${s.id}`} className="text-xs p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Draft form */}
          <div className="border-t border-border pt-4 space-y-3">
            <h4 className="font-semibold text-sm">{editingSnippetId ? 'Edit snippet' : 'Add a new snippet'}</h4>
            <div>
              <label className="block text-xs font-semibold text-foreground/70 mb-1">Title</label>
              <input
                value={snippetDraft.title}
                onChange={(e) => setSnippetDraft({ ...snippetDraft, title: e.target.value })}
                data-testid="snippet-title-input"
                maxLength={80}
                placeholder="e.g. Quote follow-up"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground/70 mb-1">Body (HTML allowed — bold, links, lists all supported)</label>
              <textarea
                value={snippetDraft.body}
                onChange={(e) => setSnippetDraft({ ...snippetDraft, body: e.target.value })}
                data-testid="snippet-body-input"
                rows={6}
                maxLength={10000}
                placeholder={"<p>Hi there,</p>\n<p>Thanks for reaching out …</p>"}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white"
              />
            </div>
            <div className="flex gap-2 justify-end">
              {editingSnippetId && (
                <Button variant="outline" size="sm" onClick={() => { setEditingSnippetId(null); setSnippetDraft({ title: '', body: '' }); }}>
                  Cancel edit
                </Button>
              )}
              <Button onClick={handleSaveSnippet} disabled={snippetSaving} data-testid="snippet-save-btn" size="sm" className="inline-flex items-center gap-1.5">
                {editingSnippetId ? <><Save className="w-3.5 h-3.5" /> Update snippet</> : <><Plus className="w-3.5 h-3.5" /> Add snippet</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp Templates */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-600" />
            WhatsApp Message Templates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-foreground/70">
            The exact copy used when staff tap "Send via WhatsApp" from a quote or invoice. Placeholders in <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">{'{{double_braces}}'}</code> are auto-filled from the record.
          </p>

          {waTemplates && ['quote', 'reminder', 'thanks'].map((key) => {
            const label = { quote: 'New Quote', reminder: 'Payment Reminder', thanks: 'Thanks / Signed Confirmation' }[key];
            const description = {
              quote: 'Sent from the quote detail page when the quote is unsigned.',
              reminder: 'Sent as an outstanding-balance nudge on unpaid invoices.',
              thanks: 'Sent from the quote detail page after the client has already signed.',
            }[key];
            return (
              <div key={key} className="border border-border rounded-lg p-4 bg-gray-50/50">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-foreground">{label}</h4>
                    <p className="text-xs text-foreground/60">{description}</p>
                  </div>
                  <button
                    onClick={() => handleWaReset(key)}
                    className="text-xs text-foreground/60 hover:text-foreground flex items-center gap-1"
                    data-testid={`wa-template-reset-${key}`}
                  >
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                </div>
                <textarea
                  value={waTemplates[key] || ''}
                  onChange={(e) => setWaTemplates({ ...waTemplates, [key]: e.target.value })}
                  rows={6}
                  maxLength={2000}
                  data-testid={`wa-template-input-${key}`}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white"
                />
                <div className="mt-2 flex justify-between items-start gap-4 text-xs">
                  <div className="flex flex-wrap gap-1">
                    <span className="text-foreground/60">Available:</span>
                    {(waTokens[key] || []).map((t) => (
                      <code key={t} className="px-1.5 py-0.5 bg-gray-200 rounded text-foreground/80">{'{{' + t + '}}'}</code>
                    ))}
                  </div>
                  <span className="text-foreground/40 whitespace-nowrap">{(waTemplates[key] || '').length}/2000</span>
                </div>
                <div className="mt-3 flex justify-end">
                  <Button
                    onClick={() => handleWaSave(key)}
                    disabled={waSavingKey === key}
                    data-testid={`wa-template-save-${key}`}
                    size="sm"
                    className="inline-flex items-center gap-1.5"
                  >
                    <Save className="w-3.5 h-3.5" />
                    {waSavingKey === key ? 'Saving…' : 'Save Template'}
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
